import useScrollAnimation from "../../../hooks/useScrollAnimation";

const problems = [
  {
    icon: "ri-phone-off-line",
    title: "MISSED CALLS",
    description:
      "67% of patient inquiries go unanswered during peak hours. Every missed call is a missed booking transferred directly to your competition.",
    metric: "67% LOST",
    index: "01",
  },
  {
    icon: "ri-time-line",
    title: "SLOW RESPONSE",
    description:
      "Practices responding within 5 minutes are 9x more likely to convert a lead. Manual follow-up processes eliminate this window entirely.",
    metric: "9X LOWER RATE",
    index: "02",
  },
  {
    icon: "ri-user-unfollow-line",
    title: "PATIENT DROP-OFF",
    description:
      "Without structured nurture systems, 58% of interested prospects disappear before booking. No follow-up means no appointment.",
    metric: "58% LOST",
    index: "03",
  },
  {
    icon: "ri-file-list-3-line",
    title: "ADMIN OVERLOAD",
    description:
      "Clinical staff spending 6+ hours daily on intake, scheduling, and follow-up. This is operational capital that belongs in patient care.",
    metric: "6H DAILY WASTE",
    index: "04",
  },
];

const ProblemSection = () => {
  const sectionRef = useScrollAnimation<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="bg-black py-24 md:py-36 px-6 md:px-16 relative overflow-hidden"
    >
      {/* Right-side abstract visual accent */}
      <div
        className="absolute right-0 top-0 bottom-0 w-px pointer-events-none"
        style={{ background: "linear-gradient(to bottom, transparent, rgba(255,209,0,0.15) 30%, rgba(255,209,0,0.15) 70%, transparent)" }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          right: "-120px",
          top: "50%",
          transform: "translateY(-50%)",
          width: 300,
          height: 300,
          background: "radial-gradient(circle, rgba(255,209,0,0.04) 0%, transparent 70%)",
          borderRadius: "50%",
        }}
      />

      {/* Header */}
      <div className="max-w-7xl mx-auto mb-16">
        <div className="reveal">
          <span
            className="text-[#FFD100] text-xs tracking-[4px] font-bold block mb-4"
            style={{ fontFamily: "Arial, sans-serif" }}
          >
            SYSTEM FAILURES
          </span>
          <h2
            className="text-white font-black uppercase text-4xl md:text-5xl leading-none"
            style={{ fontFamily: "Arial, sans-serif", letterSpacing: "-0.5px" }}
          >
            THE PROBLEMS
            <br />
            COSTING YOU PATIENTS
          </h2>
        </div>
      </div>

      {/* Cards */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
        {problems.map((problem, i) => (
          <div
            key={i}
            className={`reveal delay-${(i + 1) * 100} group relative flex flex-col gap-6 p-8 border-t-2 border-[#FFD100] bg-[#1A1A1A] transition-all duration-300 hover:bg-[#222] overflow-hidden`}
            style={{ minHeight: "340px" }}
          >
            {/* Background index number */}
            <div
              className="absolute bottom-4 right-5 font-black text-[#FFD100]/5 pointer-events-none select-none"
              style={{ fontSize: "5rem", fontFamily: "Arial, sans-serif", lineHeight: 1 }}
            >
              {problem.index}
            </div>

            {/* Top divider between cards */}
            {i < problems.length - 1 && (
              <div className="hidden lg:block absolute right-0 top-[20%] bottom-[20%] w-px bg-[#FFD100]/10" />
            )}

            {/* Icon box */}
            <div className="w-11 h-11 flex items-center justify-center border border-[#FFD100]/30 group-hover:border-[#FFD100]/60 transition-colors duration-300">
              <i className={`${problem.icon} text-[#FFD100] text-xl`} />
            </div>

            {/* Title */}
            <h3
              className="text-white font-black text-lg tracking-[2px]"
              style={{ fontFamily: "Arial, sans-serif" }}
            >
              {problem.title}
            </h3>

            {/* Divider */}
            <div className="w-8 h-px bg-[#FFD100]/40" />

            {/* Description */}
            <p
              className="text-[#E5E5E5] text-sm leading-relaxed flex-1"
              style={{ fontFamily: "Arial, sans-serif", lineHeight: "1.75" }}
            >
              {problem.description}
            </p>

            {/* Metric */}
            <div className="border-t border-[#FFD100]/15 pt-4">
              <span
                className="text-[#FFD100] text-sm font-black tracking-[2px]"
                style={{ fontFamily: "Arial, sans-serif" }}
              >
                {problem.metric}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ProblemSection;
